/*
    The main UI controller
    Implements the all map UI operations and application logic
 */

import * as L from 'leaflet';
import {UA} from "@/engine/helpers/UA.js";
import {
    MAP_STATE_CREATE_SELECT_REGION, MAP_STATE_DIALOG,
    MAP_STATE_EDIT_SELECT_REGION,
    MAP_STATE_GEOMETRY_DIALOG_ON_EDIT, MAP_STATE_NORMAL,
    mapUIState
} from "@/engine/mapUIState.js";
import {UIProperties} from "@/engine/UI/UIProperties.js";
import {mapContext} from "@/context/global.js";
import {UITemplates} from "@/engine/UITemplates.js";
import {gpsMarker} from "@/engine/gps/gpsMarker.js";
import {mapFollowTheObject} from "@/engine/gps/mapFollowTheObject.js";
import {UIDialogFactory} from "@/engine/UIcomponents/UIDialogFactory.js";
import {UIStatusPane} from "@/engine/UIcomponents/UIStatusPane.js";
import {addSidebarNoticeButton} from "@/engine/UIhelper/sidebar.js";
import {UIPDebug} from "@/engine/UIcomponents/UIPDebug.js";
import {UIMessages} from "@/engine/UIcomponents/UIMessages.js";
import {UICImportShape} from "@/engine/UIcomponents/UICImportShape.js";
import {UICMapsPanel} from "@/engine/UIcomponents/UICMapsPanel.js";
import {UICTabs} from "@/engine/UIcomponents/UICTabs.js";
import {UICShareMapIFrame} from "@/engine/UIcomponents/UICShareMapIFrame.js";
import {UICExportPdf} from "@/engine/UIcomponents/UICExportPdf.js";
import {UICExportImage} from "@/engine/UIcomponents/UICExportImage.js";
import {UICDownloadSourceFile} from "@/engine/UIcomponents/UICDownloadSourceFile.js";
import {UICDownloadGeoJson} from "@/engine/UIcomponents/UICDownloadGeoJson.js";
import {Workspace} from "@/engine/workspace.js";
import {UICFileUpload} from "@/engine/UIcomponents/UICFileUpload.js";
import {UICListCommand} from "@/engine/UIcomponents/UICListCommand.js";
import {statCollector} from "@/engine/helpers/statCollector.js";
import {UIWorkspacePane} from "@/engine/UIcomponents/UIWorkspacePane.js";
import {elog} from "@/engine/helpers/logging.js";
import {UIC3DViewer} from "@/engine/UIcomponents/UIC3DViewer.js";
import {
    Layer3D,
    LAYER_3D_SCENE_TYPE_POINT_CLOUD, LAYER_3D_SOURCE_TYPE_3DTILES,
    LAYER_3D_SOURCE_TYPE_3DTILES_11,
} from "@/engine/layers/layer3D.js";

class MapUI {

    constructor(mapContext, options) {
        this.mapContext      = mapContext;
        this.map             = mapContext.map
        this.devMode         = (options && options.devMode) ? options.devMode : false;
        this.enableDebugButtons = (options && options.debugButtons) ? options.debugButtons : false;
        this.enableDrawing = (options && options.drawing !== undefined) ? options.drawing : true;
        this.enableSidebar = (options && options.sidebar !== undefined) ? options.sidebar : true;
//        this.enableSigninPane = (options && options.signin !== undefined) ? options.signin : true;
        this.enableWorkspacesPane = (options && options.workspaces !== undefined) ? options.workspaces : true;
        this.workspacePane = null
        this.enableMessagesBox = (options && options.messagesBox !== undefined) ? options.messagesBox : true;
        this.enableMapZoom = (options && options.zoom !== undefined) ? options.zoom : true;
//        this.enableMapMove = (options && options.move !== undefined) ? options.move : true;
        this.enableControls = (options && options.controls !== undefined) ? options.controls : true;
        this.controlButtonsBar      = null;
        this.controlButtons  = Array();
        this.debugButtonsBar = null;
        this.debugButtons    = Array();
        this.debugPane       = null;
        this.mapMetrics      = null;
        this.geometryMetrics     = null;
        this.messagesBox     = null;
        this.styleEditor     = null;
        this.styleEditorDrawingStyle = null;
        this.UIsidebarMapsCollapseList = null;
        this.UIPropertiesMyName = null;
        this.UIPropertiesSettings = null;
        this.UIDialogFactory = new UIDialogFactory(mapContext.map, UA.isMobileDevice);
        this.state = new mapUIState(this) // controls current mapUI state
        this.workspaceBounds = new Map();
        this.gpsMarker       = null;

        this.statusPane         = new UIStatusPane({
            UIDialogFactory: this.UIDialogFactory,
        }).addTo(this.map);

        if (this.enableControls)
            this._initControlButtons();

        this.selectAreaBoundsStyle = {
            color: "orange",
            fillColor: "green",
            fillOpacity: 0.05,
            weight: 1,
            dashArray: 8
        }

        //debug
        if (this.devMode)
            this._initDebugFacility();

        if (this.enableSidebar)
            this._initSidebar();
/*
        if (this.enableSigninPane) {
            this.signinPane      = new UISignInPane( {
                identityGetter: function () {
                    return mapContext.identity;
                },
                signInCb: mapContext.signIn,
                signOutCb: mapContext.signOut,
                position: "topright"
            }).addTo(this.mapContext.map)
        }
 */

        if (this.enableWorkspacesPane)
            this.workspacePane = new UIWorkspacePane( {
               mapContext: this.mapContext,
            }).addTo(this.mapContext.map)

        // workspace operations calls from UI components
        this.workspaceActions = {
            hide: function (ws) {
                ws.hide()
                //need to save workspaces visibility state
                this.mapContext.UIContext.set({"hidden-workspaces": this.mapContext.getWorkspacesHidden().map(function (v) {return v.id})})
            }.bind(this),
            show: function (ws) {
                ws.show()
                //need to save workspaces visibility state
                this.mapContext.UIContext.set({"hidden-workspaces": this.mapContext.getWorkspacesHidden().map(function (v) {return v.id})})
            }.bind(this),
            delete: function (ws) {
                this.createDeleteWorkspaceWindowDialog(ws)
            }.bind(this),
            export: function (ws) {
                if (ws.isVisible() && ws.bounds)
                    this.createAreaExportDialog(ws)
            }.bind(this),
            share: function (ws) {
                this.createShareWindowDialog(ws)
            }.bind(this),
            edit: function (ws) {
                this.createEditWorkspacePropertiesWindowDialog(ws);
            }.bind(this),
            addLayer: function (ws) {
                this.state.toggleMapState(MAP_STATE_DIALOG)
                let UIC = new UICFileUpload({worker: this.mapContext.RPCUploadEntity.bind(this.mapContext), uploadType: 'any', target: ws.id})
                this.UIDialogFactory.createUICDialog(UIC, {onClose: function () {
                        this.state.toggleMapState(MAP_STATE_NORMAL)
                    }.bind(this)})
            }.bind(this),
            viewTo: function (ws) {
                this.mapContext.mapViewTo(ws);
            }.bind(this)
        }

        this.registerUIActions(this.mapContext, this.workspaceActions, "ws")

        // layer operations calls from UI components
        this.layerActions = {
            hide: function (l) {
                l.hide();
                let hls = this.mapContext.UIContext.get("hidden-layers")
                if ( ! (hls instanceof Array)) {
                    hls = [];
                }
                if (!hls.includes(l.id)) {
                    hls.push(l.id);
                }
                this.mapContext.UIContext.set({"hidden-layers": hls}, true);
            }.bind(this),
            show: function (l) {
                l.show();
                let hls = this.mapContext.UIContext.get("hidden-layers")
                if ( ! (hls instanceof Array)) {
                    hls = [];
                }
                let idx = hls.indexOf(l.id);
                if (idx !== -1) {
                    hls.splice(idx, 1);
                }
                this.mapContext.UIContext.set({"hidden-layers": hls}, true);

            }.bind(this),
            delete: function (l) {
                this.createDeleteLayerWindowDialog(l)
            }.bind(this),
            edit: function (l) {
                this.createEditLayerPropertiesWindowDialog(l);
            }.bind(this),
            export: function (l) {
                if (l.isVisible() && l.getBounds())
                    this.createAreaExportDialog(l)
            }.bind(this),
            viewTo: function (l) {
                this.mapContext.mapViewTo(l);
            }.bind(this),
            style: function (l) {
                this.mapContext.mapViewTo(l);
                this.createStyleEditorDialogForVectorLayer(l);
            }.bind(this)
        }

        this.registerUIActions(this.mapContext, this.layerActions, "layer")

        // adds map zoom controls
        if (this.enableMapZoom)
            L.control.zoom({position: "topright"}).addTo(this.map);

        // adds drawings controls
        if (this.enableDrawing)
            this._initDrawingControls();

        // defines function that evaluate viewport for dialog windows (it can differ depends on opened sidebar, etc)
        this.UIDialogFactory.setVieportPadding( function () {
            let rightEl = mapContext.map._container.querySelector(".leaflet-right")
            let leftEl = mapContext.map._container.querySelector(".leaflet-sidebar-tabs")
            let padding = {}
            if (rightEl) {
                padding.right = rightEl.offsetWidth
            }

            if (leftEl) {
                let r = leftEl.getBoundingClientRect()
                padding.left = r.left + r.width // temp solution, no time to calc the exact sidebar position
                if (!leftEl.parentElement.classList.contains('collapsed')) {
                    padding.left += 10
                }
            }
            return padding
        })

        // adds messages display box
        if (this.enableMessagesBox) {
            this.messagesBox = new UIMessages(
                document.querySelector(".messages-box"),
                this.getContentTemplate("messagesBox"),
                {
                    ownerUID: "",
                    unreadChangedCallback: function(n) {
                        try {
                            let el = this.sidebar._getTab("messages");
                            if (!el)
                                return;
                            this.messagesBox.badge(el, n);
                        } catch (e) {

                        }
                    }.bind(this),
                    sendMessageCallback: null,
                    storage: {
                        getFrom: mapContext.storage.getMessagesFrom.bind(mapContext.storage)
                    },
                }
            );
        }

        this.UIWorkingModeOptions = new UIProperties(
            this.getContentTemplate("workingModeOptions"),
            {
                propertiesFactory: async function (prop) {
                    let d = this.mapContext.UIContext.data();
                    prop.showPublicMaps = d.showPublicMaps
                    prop.myLocationMode = d.myLocationMode;
                }.bind(this),
                togglersCallbacks: {
                    "showPublicMaps": function (val, fail) {
                        this.mapContext.UIContext.set({"showPublicMaps": val});
                    }.bind(this),
                    "myLocationMode" : function (val, fail) {
                        this.mapContext.UIContext.set({"myLocationMode": val});
                    }.bind(this),
                }
            }
        )

        let gps = this.mapContext.gpsStateMachine
        this.mapFollowTheObject = new mapFollowTheObject(this.map)
        if (gps) {
            gps.on("start", function () {
                // adds location pane
                this.statusPane.update('gpsHeader', {})
                // adds marker and button on first location event
                gps.once("location", function (ev) {
                    // adds marker
                    if (! this.gpsMarker)
                        this.gpsMarker = new gpsMarker(ev.location, this.map)

                    let latLng = this.gpsMarker.marker.getLatLng()
                    if (latLng)
                        this.map.setView(this.gpsMarker.marker.getLatLng());

                    // adds button
                    this.controlButtonsBar.addButtonAfter(this.controlButtons[0])
                }.bind(this))
            }.bind(this))
            gps.on("stop", function () {
                // removes button
                this.controlButtonsBar.removeButton(this.controlButtons[0].options.id)
                // removes location pane
                this.statusPane.update('gpsHeader', null)
                // removes marker
                if (this.gpsMarker)
                    this.gpsMarker.remove()
            }.bind(this))
            gps.on("located", function (ev) {
                //this.mapFollowTheObject.startFollowing(this.gpsMarker.marker)
            }.bind(this))
            gps.on("location", function (ev) {
                // updates location pane data && marker position
                if (this.gpsMarker)
                    this.gpsMarker.setLocation(ev.location)
                this.statusPane.update('gpsHeader', ev.location)
            }.bind(this))
            gps.on("error", function (ev) {
                this.statusPane.update('gpsHeader', ev)
                // updates location pane data
            }.bind(this))
        }

        this.mapContext.on("layer:wrongSRS", function (ev) {
            this.createLayerWrongSRSWindowDialog(ev.layer)
        }, this)
    }

    // add actions event handlers
    registerUIActions(eventObject, actions, actionSubject) {
        if (!eventObject || !eventObject.on) {
            elog("registerUIActions: something wrong, eventObject isn't defined")
        }
        for (let a in actions) {
            let keys = []
            if (actionSubject)
                keys.push(actionSubject)
            keys.push("ui:action", a)
            eventObject.on(keys.join(":"), function (e) {
                actions[a](e.data)
            })
        }
    }
    setObserverMode(v) {
        if (v) {
            this.sidebar._container.classList.add("observer-mode");
        } else {
            this.sidebar._container.classList.remove("observer-mode");
            //this.controlButtonsBar._container.classList.remove("observer-mode");
        }
    }

    signInOutUIUpdate() {
        if (! this.sidebar)
            return
        if ( ! mapContext.identity ) {
            addSidebarNoticeButton(
                this.sidebar,
                'sign-in',
                'Sign-in',
                'sign-in-alt',
                function () {
                    this.mapContext.signIn();
                }.bind(this))
            this.sidebar.removePanel('sign-out')
//            this.sidebar.disablePanel('sign-out')
        } else {
            this.sidebar.removePanel('sign-in')
            this.sidebar.enablePanel('sign-out')
        }
    }

    // setDrawingDefaultStyle set default drawing style to geoman drawing module
    setDrawingDefaultStyle(styles) {
        if (styles) {
            //set draw options for "Rectangle", "Polygon", "Circle"
            this.map.pm.setPathOptions(styles, {
                ignoreShapes: ['Line', "Marker"],
            });

            //set draw options for 'Line'
            let s = Object.assign({}, styles);
            s.fill = false;
            delete s["fillOpacity"];
            this.map.pm.setPathOptions(s, {
                ignoreShapes: ["Rectangle", "Polygon", "Circle", "Marker"],
            });
        }
    }
    /**
     * inits shapes drawing controls and adds buttons to the map
     * @param options
     * @private
     */
    _initDrawingControls() {
        //add buttons
        this.map.pm.addControls({
            position: 'topright',
            drawCircleMarker: false,
            drawText: false,
            cutPolygon: false, //disabled, because there is some problem with layer updates after cut (polygon isn't saved to wfst and stopped to get pm:events), need some debugging
        })

        //add shape import button
        this.map.pm.Toolbar.createCustomControl({
            name: "importFromFile",
            block: "draw",
            title: "Load shape from file",
            className: "leaflet-control-button-file",
            toggle: false,
            onClick: (e) => {
                this.UIDialogFactory.createUICDialog(new UICImportShape(), {maxWidth: 350})
            },
            disableByOtherButtons: true,
        })

        let setLayerStyleToDefaultStyle = function (e) {
            e.target.setStyle(mapContext.drawingStyleContext.data())
            e.target._maphubVectorLayer.saveFeature(e.target)
        }

        let isRelevantToStyle = function (l) {
            if (l && (l instanceof L.Polygon || l instanceof L.Polyline || l instanceof L.Circle))
                if (l._maphubVectorLayer && l._maphubVectorLayer.editable)
                    return true
            return false
        }

        // calls from style editor
        let enableLayersChangeStyle = function () {
            this.map._container.style.cursor = 'crosshair';
            //put listeners clicks on layers
            this.map.eachLayer((layer) => {
                if (isRelevantToStyle(layer)) {
                    layer.on('click', setLayerStyleToDefaultStyle, this);
                }
            });
        }.bind(this)

        // calls from style editor
        // disable layers style map modifying mode
        let disableLayersChangeStyle = function () {
            this.map._container.style.cursor = '';
            //restore cursor
            this.map.eachLayer((layer) => {
                layer.off('click', setLayerStyleToDefaultStyle, this);
            });
        }.bind(this)

        //add styleEditor button
        this.map.pm.Toolbar.createCustomControl({
            name: "styleEditor",
            block: "custom",
            title: "Edit style",
            className: "leaflet-control-styleeditor-interior",
            actions: [
                'finishMode',
                {
                    text: 'Edit default style',
                    onClick: (e) => {
                        this.createStyleEditorDialogForDrawing()
                        //hide button menu
                        mapContext.map.pm.Toolbar.buttons.styleEditor._clicked()
                    },
                },
            ],
            onClick: (e) => {
                // change cursor
                if (! this.map.pm.Toolbar.buttons.styleEditor._button.toggleStatus) {
                    enableLayersChangeStyle()
                } else {
                    disableLayersChangeStyle()
                }

            },
            disableByOtherButtons: true,
        })

        //Initialize the StyleEditor
        this.styleEditor = L.control.styleEditor({
            forms: {
                'geometry': {
                    'color': { 'boolean': true, options: {title: "Line color"}},
                    'opacity': { 'boolean': true, options: {title: "Line opacity"}},
                    'weight': { 'boolean': true, options: {title: "Line weight"}},
                    'dashArray': { 'boolean': true, options: {title: "Line type"}},
                    'fillColor': { 'boolean': true, options: {title: "Fill color"}},
                    'fillOpacity': { 'boolean': true, options: {title: "Fill opacity"}},
                    'popupContent': false
                }
            }
        }).createUi(this.map);

        // add backup feature for styleEditor
        // allows restore the element style options when Cancel (Restore) button is pressed
        // and also allow to check if some options were changed after styleeditor close
        this.map.on('styleeditor:editing', function(leafletLayer){
            if (! leafletLayer._maphubVectorLayer ||
                ! leafletLayer._maphubVectorLayer._styleOptions)
                return
            this.styleEditor.options._backupStyle = leafletLayer._maphubVectorLayer._styleOptions(leafletLayer)
        }.bind(this));

        //do geometry save when styleeditor is closed
        this.map.on('styleeditor:hidden', function(leafletLayer){
            if (leafletLayer._maphubVectorLayer && this.styleEditor.options._backupStyle !== undefined) {
                let curStyles = leafletLayer._maphubVectorLayer._styleOptions(leafletLayer)
                // save geometry if any of style was changed
                let doSave = false
                if (Object.keys(this.styleEditor.options._backupStyle).length !==
                    Object.keys(curStyles).length) {
                    doSave = true;
                } else {
                    for (let style in this.styleEditor.options._backupStyle) {
                        if (curStyles[style] !== this.styleEditor.options._backupStyle[style]) {
                            doSave = true;
                            break
                        }
                    }
                }
                if (doSave) {
                    if (leafletLayer._maphubVectorLayer.saveFeature)
                        // WFST layer
                        leafletLayer._maphubVectorLayer.saveFeature(leafletLayer)
                    else if (leafletLayer._maphubVectorLayer.saveStyle) {
                        // regular VectorLayer
                        leafletLayer._maphubVectorLayer.saveStyle()
                    }
                }
            } else {
                disableLayersChangeStyle()
            }
        }.bind(this));

        // creates L.polygon where styleeditor stores default drawing style
        this.styleEditorDrawingStyle = L.polygon([])
        // saves style options to drawingStyleContext
        this.styleEditorDrawingStyle.on('styleeditor:changed', function(leafletLayer){
            // save style to persistent storage && set geoman default drawing style
            let drawingOptions = mapContext.drawingStyleContext.data()
            for (let style in drawingOptions) {
                drawingOptions[style] = leafletLayer.options[style]
            }
            mapContext.drawingStyleContext.set(drawingOptions)
            this.setDrawingDefaultStyle(drawingOptions)
            console.log( "default drawing style was changed");
        }.bind(this));

    }

    //init left sidebar
    _initSidebar() {
        let panes = [
            {
                id: 'maps',//'settings',
                tab: '<i class="fa fa-bars"></i>',
                pane: '<div></div>',
                title: 'Maps',
                position: 'top',
                onOpen: function(id) {
                    let container = this.sidebar._paneContainer.querySelector('#' + id + '>div');

                    if (this.UIsidebarMapsCollapseList !== null) {
                            this.UIsidebarMapsCollapseList.updateContent()
                    } else {
                        this.UIsidebarMapsCollapseList = new UICMapsPanel({
                            contextStorage: this.mapContext.UIContext,
                        })
                        this.UIsidebarMapsCollapseList.open(container)
                    }

                    if (!this.mapContext.identity && !container.querySelector("div[id='signinNotification']")) {
                        let signinNotification = document.createElement("div")
                        signinNotification.id = 'signinNotification'
                        signinNotification.innerHTML = "<span>Please <a href=\"\">sign in</a> to save your personal work and drawings for a long time</span>"
                        signinNotification.classList.add("smallInfo")
                        signinNotification.querySelector("a").addEventListener(
                            "click",
                            function(e) {
                                e.preventDefault();
                                mapContext.signIn()
                            })
                        container.appendChild(signinNotification)
                    }
                }.bind(this),
                onClose: function (id) {
                    let container = this.sidebar._paneContainer.querySelector('#' + id + '>div');
                }.bind(this)
            },
            {
                id: 'mapLayers',
                tab: '<i class="leaflet-control-layers-toggle" style="width: unset;">&nbsp;</i>',
                title: 'Map layers',
                position: 'top',
                class: (Object.keys(this.mapContext.baseMapLayers).length > 0 ? 'tab-visible':'') + ' tab-hide-expanded',
                button: function() {
                    this.mapContext.mapSetNextLayer();
                }.bind(this),
            },
            {
                id: 'addNewMap',
                tab: '<span class="icon-stack"><i class="far fa-square"></i><i class="fas fa-plus" style="left: 0em; top: 0.5em;"></i></span>',
                //'<i class="fa fa-map-marked-alt"></i>',
                title: 'Add the object',
                position: 'top',
                class: 'tab-visible hide-on-observer-mode',
                button: function() {
                    this.createNewWorkspaceWindowDialog()
                }.bind(this),
            },
            {
                id: 'messages',
                tab: '<i class="fa fa-envelope"></i>',
                title: 'Messages',
                position: 'top',
                class: 'tab-visible tab-hide-expanded',
                button: function() {
                    if (this.messagesBox.isActive()) {
                        this.messagesBox.hide()
                    } else {
                        this.messagesBox.show()
                    }
                }.bind(this),
            },

            {
                id: 'options',
                tab: '<i class="fas fa-cog"></i>',
                pane: '<div></div>',
                title: 'Options',
                position: 'top',
                onOpen: function(id) {
                    let container = this.sidebar._paneContainer.querySelector('#' + id + '>div');
                    container.appendChild(this.UIWorkingModeOptions.content());
                }.bind(this),
                onClose: function (id) {

                }.bind(this)
            },

            {
                id: 'about',
                tab: '<i class="fa fa-question-circle"></i>',
                pane: this.getContentTemplate("aboutContent"),
                title: 'About',
                position: 'top',
                onOpen: function() {
                    console.log("about bar is openned");
                },
                onClose: function () {
                    console.log("about bar is closed");
                }
            },
            {
                id: 'sign-out',
                tab: '<i class="fa fa-sign-out-alt"></i>',
                pane: '<div></div>',
                title: 'Sign Out',
                position: 'top',
                onOpen: function(id) {
                    let container = this.sidebar._paneContainer.querySelector('#' + id + '>div');

                    let identity = mapContext.identity

                    if (! identity) {
                        // something got wrong
                        this.signInOutUIUpdate();
                        this.sidebar.close();
                        return
                    }

                    let name = "";
                    if (identity.firstName)
                        name += identity.firstName + ' '
                    if (identity.lastName)
                        name += identity.lastName + ' '

                    container.innerHTML = this.getContentTemplate("signOut", {name: name})

                    let a = container.querySelector("a")

                    L.DomEvent
                        .on(a, 'click', L.DomEvent.preventDefault)
                        .on(a, 'click', function (item) {
                            let target = item.currentTarget.getAttribute("href");
                            if (target === "#signout") {
                                this.mapContext.signOut();
                            }
                        }.bind(this));
                }.bind(this)
            }
        ];

        /*
            prepare the left control-pane
         */
        //create DOM from template
        let node = document.createElement('div');
        node.className = "control-pane-wrapper collapsed"
        node.innerHTML = this.getContentTemplate("mainLeftControlWrapper");
        //let wrapper = node.firstElementChild;
        this.map._container.insertBefore(node, this.map._container.firstChild);

        this.sidebar = L.control.sidebar({
            autopan: false,
            closeButton: true,
            container: this.map._container.querySelector(".leaflet-sidebar"),
            position: 'left',

        }).addTo(this.map);

        panes.forEach(function (pane) {
            this.sidebar.addPanel(pane);
        }.bind(this));

        //panes open/close events handling
        let fireHandler = function (id, handler) {
            //fire handler for panel
            panes.forEach(function (pane) {
                if (pane.id === id && pane[handler] && typeof pane[handler] === "function")
                    pane[handler](id);
            });
        };

        //switching panes on sidebar
        this.sidebar.on('content', function (e) {
            if (this.sidebar.activePane && this.sidebar.activePane !== e.id)
                fireHandler(this.sidebar.activePane, 'onClose');
            this.sidebar.activePane = e.id;
            fireHandler(e.id, 'onOpen');
        }.bind(this));

        //sidebar closing
        this.sidebar.on('closing', function (e) {
            if (this.sidebar.activePane) {
                fireHandler(this.sidebar.activePane, 'onClose');
            }
            this.sidebar.activePane = null;
            //in order leaflet-left controls move right according sidebar width
            document.querySelector(".control-pane-wrapper").classList.add("collapsed");
            document.querySelector(".control-pane-wrapper").classList.remove("expanded");

        }.bind(this));

        //sidebar opening
        this.sidebar.on('opening', function (e) {
            document.querySelector(".control-pane-wrapper").classList.remove("collapsed");
            document.querySelector(".control-pane-wrapper").classList.add("expanded");
        }.bind(this))

        this.sidebar.hide = function (id) {
            this._getTab(id).classList['remove']("tab-visible");
        }.bind(this.sidebar)

        this.sidebar.show = function (id) {
            this._getTab(id).classList['add']("tab-visible");
        }.bind(this.sidebar)

        this.sidebar.getPane = function (id) {
            let i
            for (i = 0; i < this._panes.length; i++) {
                if (this._panes[i].id === id) {
                    return this._panes[i]
                }
            }
            return null
        }.bind(this.sidebar)

        this.UIDialogFactory.addOnDialogOpenHandler( function () {
            this.sidebar.close()
        }.bind(this))
    }

    /**
     * init map contol buttons (like zoom and map center buttons)
     * @private
     */
    _initControlButtons() {
        //build control bars

        this.controlButtons.push(L.control.easyButton( {
            id: "centerAtMyLocation",
            states: [{
                //on
                stateName: 'off',
                title: "Center and follow for my position",
                icon: 'fa-crosshairs',
                onClick: function(btn, map) {
                    this.mapFollowTheObject.startFollowing(this.gpsMarker.marker, function () {
                        // interrupted
                        btn.state('off')
                    });
                    btn.state('on')
                }.bind(this)
            },
            {
                //off
                stateName: 'on',
                title: "Turning off a following for my position",
                icon: 'fa-crosshairs',
                onClick: function(btn, map) {
                    this.mapFollowTheObject.stopFollowing();
                }.bind(this)
            }],
            class: "hide-on-observer-mode"
        }));

        this.controlButtons.push( L.control.easyButton({
            id: "observeAllPositions",
            states: [{
                title: "Move the map to observe all positions",
                icon: 'fa-arrows-alt',
                onClick: function(btn, map) {
                    this.mapContext.mapViewToObserveAllWorkspaces();
                }.bind(this)
            }]
        }));

        this.controlButtonsBar = L.control.easyBar([this.controlButtons[1]], {position: "topright"}).addTo(this.map);

        //switch to horizontal orientation
        L.DomUtil.addClass(this.controlButtonsBar.container, "easy-button-horizontal-bar");
    }

    _initDebugFacility() {

        //map debug metrics
        this.mapMetrics = (typeof statCollector == "function") ? new statCollector(
            "map",
            {
                viewArea:  function () {return this.map.getBounds()}.bind(this),
                zoom: function () {return this.map.getZoom()}.bind(this),
                pointer: ""
            }
        ) : null;

        //selected geometry metrics
        this.geometryMetrics = (typeof statCollector == "function") ? new statCollector(
            "geometry",
            {
                id: "",
                workspace: "",
                name: "",
                boundingBox: "",
            }
        ) : null;

        this.debugPane = this.devMode ? new UIPDebug([this.mapContext.stat, this.mapMetrics, this.geometryMetrics, this.mapContext.gpsStateMachine.stat]).addTo(this.map) : null;

        let pointerMoveHandler = function (l) {
            this.mapMetrics.set("pointer", l.latlng);
        }

        this.debugButtons.push(L.control.easyButton({
            states: [{
                title: "Turn on/off debug status dashboard",
                icon: 'fa-bug',
                onClick: function() {
                    if (! this.debugPane)
                        return

                    if (this.debugPane.isVisible()) {
                        this.map.off('mousemove', pointerMoveHandler, this)
                        this.debugPane.hide()
                    } else {
                        this.map.on('mousemove', pointerMoveHandler, this)
                        this.debugPane.show()
                    }


                }.bind(this)
            }]
        }));

        this.debugButtons.push(L.control.easyButton({
            states: [{
                title: "Delete all cookies",
                icon: 'fa-cookie-bite',
                onClick: function() {
                    this.UIDialogFactory.modalYesNoWindow( {
                        title:  "All site's cookies will be deleted",
                        content: "Are you sure?",
                        yesCaption: "Blast-it-all!",
                        noCaption: "No, I missed",
                        yes: function () {
                            sm.notice("All site's cookies has been deleted");
                            mapApi.deleteAllCookies();
                            this.mapContext.storage.Db().then(function (db) {
                                db.clear("context")
                                db.clear("messages")
                            })
                            return true;
                        }.bind(this),
                        no: function () {
                            return true;
                        },
                    });
                }.bind(this)
            }]
        }));

        this.debugButtons.push(L.control.easyButton({
            states: [{
                title: "Open 3D object demo",
                icon: 'fa-cube',
                onClick: function() {
                    const layer = new Layer3D({
                        id: "debug-3d-tiles-point-cloud",
                        name: "Debug 3D Tiles point cloud",
                        workspace: this.mapContext,
//                        file: "/demo/3dtiles/pointcloud/amber/",
//                        sourceType: LAYER_3D_SOURCE_TYPE_3DTILES,
//                        sceneType: LAYER_3D_SCENE_TYPE_POINT_CLOUD,
                        file: "/demo/3dtiles/pointcloud/amber2/",
                       sourceType: LAYER_3D_SOURCE_TYPE_3DTILES_11,
                        sceneType: LAYER_3D_SCENE_TYPE_POINT_CLOUD,
//                        file: "/demo/3dtiles/pointcloud/amber3/",
//                        sourceType: LAYER_3D_SOURCE_TYPE_3DTILES_11,
//                        sceneType: LAYER_3D_SCENE_TYPE_POINT_CLOUD,
                        visible: true,
                    });
                    this.UIDialogFactory.createUICDialog(new UIC3DViewer({
                        layer: layer,
                    }), {
                        maxWidth: 960,
                        minWidth: 520,
                    });
                    mapContext.debugObjects.layer3d = layer;
                }.bind(this)
            }]
        }));

        if (this.enableDebugButtons) {
            this.debugButtonsBar = L.control.easyBar(this.debugButtons, {position: "topright"}).addTo(this.map);
            L.DomUtil.addClass(this.debugButtonsBar.container, "easy-button-horizontal-bar");
        }

        //
        document.addEventListener('DOMContentLoaded', this.onDocumentLoad.bind(this));
    }

    /**
     *
     * @param UIProperties
     * @param onCancel
     * @param mapState
     */
    createPropertiesWindowDialog(UIProperties, onCancel, setMapState) {
        // prevent dialog appearance when sidebar is opened, and instead opens sidebar configuration pane
        /*
        if (this.sidebar.activePane) {
            if (this.sidebar.activePane !== 'settings') {
                this.sidebar.open('settings');
            }
            this.sidebar._paneContainer.querySelector('.propertiesPane input[data-bind=name]').focus();
            return;
        }

         */
        let mapState = this.state
        mapState.toggleMapState(setMapState ? setMapState : MAP_STATE_DIALOG)
        let window = this.UIDialogFactory.createDialog("properties", {
            className: "propertiesWindow control-window",
            title: '',
            content: UIProperties.content(),
            closeButton: false,
            maxWidth: 350,
            prompt: {
                buttonOK: "OK",
                buttonCancel: "CANCEL",
                callback: function (ev) {
                        mapState.toggleMapState(MAP_STATE_NORMAL)
                        return UIProperties.action(ev);
                    },
                cancel: function (ev) {
                    mapState.toggleMapState(MAP_STATE_NORMAL)
                    return (typeof onCancel === "function" ? onCancel(ev) : true);
                }
            }
        });
        //fire window OK action  when Enter key is pressed on input field
        UIProperties.registerInternalActionCallback( function () {
            // todo possible need to fix for case of several input fields
            document.querySelector('.propertiesWindow').dispatchEvent(new Event('L.Control.Window.OK'))
        });
        window.content().querySelector('input[tabindex="0"]').focus();
    }

    createEditWorkspacePropertiesWindowDialog(workspace) {
        this.createPropertiesWindowDialog( new UIProperties(
            this.getContentTemplate("workspaceProperties", {mine: workspace.mine}),
            {
                actionCb: function (prop) {
                    workspace.updateProperties(prop);
                },
                propertiesFactory: async function (prop) {
                    prop.name = workspace.name;
                    prop.description = workspace.description;
                    prop.public = workspace.public;
                    prop.publicDownload = workspace.publicDownload;
                    prop.bounds = this.workspaceBounds.get(workspace.id).isVisible()
                }.bind(this),
                togglersCallbacks: {
                    "bounds" : function (val, fail) {
                        val ? this.workspaceBounds.get(workspace.id).show() : this.workspaceBounds.get(workspace.id).hide() ;
                    }.bind(this),
                }
            },
        ));
    }

    createEditLayerPropertiesWindowDialog(l) {
        let layerRecreateEvent = function (e) {
            // console.log(`debug: layer removed while dialog is openned ${l.id}`)
            // todo need to update dialog content that can depend on changed layer data
            l = e.layer
        }
        this.createPropertiesWindowDialog( new UIProperties(
            this.getContentTemplate("layerProperties", l),
            {
                actionCb: function (prop) {
                    this.mapContext.off("layer:recreate", layerRecreateEvent, this)
                    l.updateProperties(prop);
                }.bind(this),
                propertiesFactory: async function (prop) {
                    prop.name = l.name
                    if (l.typeVector) {
                        prop.elevation = l.typeElevation
                        prop.hasElevation = l.hasElevation
                    }
                }.bind(this),
                togglersCallbacks: {
                    "elevation" : function (val, fail) {
                        l.updateProperties({elevation: val})
                    }.bind(this),
                }
            },
        ), function () {
            this.mapContext.off("layer:recreate", layerRecreateEvent, this)
            return true;
        }.bind(this));
        this.mapContext.on("layer:recreate", layerRecreateEvent, this)
    }

    createEditGeometryPropertiesWindowDialog(leafletLayer, succ, fail) {
        this.createPropertiesWindowDialog( new UIProperties(
            this.getContentTemplate("markerProperties"),
            {
                actionCb: function (prop) {
                    try {
                        leafletLayer.setProperties(prop)
                    } catch (e) {
                        // when vector layer isn't yet defined
                    }
                    if (typeof succ === "function")
                        succ(prop);
                    return true;
                },
                propertiesFactory: async function (prop) {
                    try {
                        prop.descr = leafletLayer.getProperty('descr');
                        if (prop.descr === undefined)
                            prop.descr = ""
                    } catch (e) {
                        // when vector layer isn't yet defined
                        prop.descr = "";
                    }
                }.bind(this)
            },
        ), function () {
            if (typeof fail === "function")
                fail();
            return true;
        });
    }

    createLayerWrongSRSWindowDialog(layer) {
        this.createPropertiesWindowDialog( new UIProperties(
            UITemplates.getContent("layerWrongSRS", {name: layer.name}),
            {
                actionCb: function (prop) {
                    if (typeof layer.updateProperties === "function")
                        if (layer.updateProperties(prop)) {
                            this.mapContext.once("layer:recreate", function (e) {
                                if (e.layer.id === layer.id)
                                    sm.noticeMessage(`Layer '${layer.name}' has been updated`)
                            }, this)
                        }
                }.bind(this),
                propertiesFactory: async function (prop) {
                    if (layer.srs) {
                        let n = layer.srs.match(/\d+$/);
                        if (n && n[0] !== "404000") {
                            prop.srs = n[0];
                            return
                        }
                    }
                    prop.srs = "";
                }.bind(this)
            },
        ));
    }

    createShareWindowDialog(workspace) {
        workspace.shareLinks().then( shareLinks => {
            let fullAccessLink = ''
            let readAccessLink = ''
            for (let shareLink of shareLinks) {
                if (shareLink.sharingType === 1) {
                    readAccessLink = shareLink.url;
                } else if (shareLink.sharingType === 2) {
                    fullAccessLink = shareLink.url;
                }
            }

            let UIC = new UICTabs({
                title: "Share the map",
                cancelActionName: "CLOSE",
                mainActionName: "",
                items: [
                    {   name: 'Observe',
                        content: UITemplates.getContent('url-to-share', {
                            value: readAccessLink,
                            shareTypeRO: true,
                        })
                    },
                    {
                        name: 'Full access',
                        content: UITemplates.getContent('url-to-share', {
                            value: fullAccessLink,
                            shareTypeRW: true,
                        })
                    },
                    {
                        name: 'IFrame',
                        content: new UICShareMapIFrame({
                            workspace: workspace,
                            link: readAccessLink
                        })
                    },
                ]
            })
            this.UIDialogFactory.createUICDialog(UIC, {
                position: 'topLeft'
            })
        })
    }

    createPdfExportDialog(layer) {
        let UIC = new UICExportPdf({
            pdf: this.mapContext.pdf,
            map: this.map,
            area: layer
        })
        this.UIDialogFactory.createUICDialog(UIC)
    }

    createImageExportDialog(layer) {
        let UIC = new UICExportImage({
            pdf: this.mapContext.pdf,
            map: this.map,
            area: layer
        })
        this.UIDialogFactory.createUICDialog(UIC)
    }

    /**
     * createExportDialog to an any area that have getBounds method
     * @param area
     */
    createAreaExportDialog(area) {
        this.mapContext.mapViewTo(area)
        let exportOptions = {
            pdf: this.mapContext.pdf,
            map: this.map,
            area: area.getBounds(),
            rasterArea: true,
        }

        let UIC = new UICTabs({
            id: 'areaExport',
            title: "Map export",
            items: [{
                    name: 'Picture',
                    content: new UICExportImage(exportOptions),
                    picture: UITemplates.getContent('svg-export-picture'),
                },
                {   name: 'Pdf',
                    content: new UICExportPdf(exportOptions),
                    picture: UITemplates.getContent('svg-export-pdf'),
                },
                // area should be a workspace object
                {   name: 'Source file',
                    content: new UICDownloadSourceFile({layer: area}),
                    picture: UITemplates.getContent('svg-export-source-file'),
                },
                // area should be a workspace object
                {   name: 'GeoJson',
                    content: new UICDownloadGeoJson({
                            layer: (area instanceof Workspace) ?
                                area.getVectorLayers().flatMap( vl => {return vl.typeDrawing ? vl.layer.getLayers() : []}):
                                null
                        }
                    ),
                    picture: UITemplates.getContent('svg-file'),
                }
            ]
        })
        this.UIDialogFactory.createUICDialog(UIC, {
            position: 'topLeft'
        })
    }

    createGeometryExportDialog(layer) {
        let exportOptions = { pdf: this.mapContext.pdf, map: this.map, area: layer }
        let UIC = new UICTabs({
            id: 'objectExport',
            title: "Save / export object",
            items: [
                {
                    name: 'Picture',
                    picture: UITemplates.getContent('svg-export-picture'),
                    content: new UICExportImage(exportOptions)
                },
                {
                    name: 'Pdf',
                    picture: UITemplates.getContent('svg-export-pdf'),
                    content: new UICExportPdf(exportOptions)
                },
                {
                    name: 'GeoJson',
                    picture: UITemplates.getContent('svg-file'),
                    content: new UICDownloadGeoJson({layer: layer})
                }
            ]
        })
        this.UIDialogFactory.createUICDialog(UIC, {
            position: 'topLeft'
        })
    }

    createVertexCoordinateEditDialog(leafletLayer) {
        this.createPropertiesWindowDialog( new UIProperties(
            // todo FIX UIHelper.copyToClipboard,
            this.getContentTemplate("coordinateProperties", { onClick: "this.select(); UIHelpers.copyToClipboard(this)"}),
            {
                actionCb: function (prop) {
                    leafletLayer.setLatLng(L.latLng(prop.lat, prop.lng))
                },
                propertiesFactory: async function (prop) {
                    prop.lat = leafletLayer.getLatLng().lat.toFixed(7)
                    prop.lng = leafletLayer.getLatLng().lng.toFixed(7)
                }.bind(this)
            },
        ), null, MAP_STATE_GEOMETRY_DIALOG_ON_EDIT);
    }

    createSelectionArea(onFinish, onCancel) {
        let defLayer = this.map.pm.getGlobalOptions().layerGroup
        // override undefined defLayer to null (need to restore undefined layerGroup option correctly)
        if (!defLayer)
            defLayer = null

        let selectRectLayerGroup = this.mapContext.selectRectLG
        this.map.pm.setGlobalOptions({
            layerGroup: selectRectLayerGroup
        })

        let defOptions = {
            continueDrawing: mapContext.map.pm.Draw.Rectangle.options.continueDrawing,
            pathOptions: Object.assign({}, mapContext.map.pm.Draw.Rectangle.options.pathOptions)
        }

        this.state.toggleMapState(MAP_STATE_CREATE_SELECT_REGION)
        this.map.pm.enableDraw("Rectangle", {
            continueDrawing: false,
            pathOptions: this.selectAreaBoundsStyle
        });
        this.map.once("pm:drawend", function (ev) {
            // restore default rectangle drawing options
            this.map.pm.setGlobalOptions({layerGroup: defLayer})
            this.map.pm.Draw.Rectangle.options.continueDrawing = defOptions.continueDrawing
            this.map.pm.Draw.Rectangle.options.pathOptions = defOptions.pathOptions
            // area creating was canceled
            if (!this.state.isMapState(MAP_STATE_EDIT_SELECT_REGION))
                this.state.toggleMapState(MAP_STATE_NORMAL)
        }.bind(this))

        // process cancel operation
        this.map.once("pm:actionclick", function (ev) {
            if (ev.text === "Cancel" && ev.btnName === "drawRectangle") {
                if (onCancel) {
                    onCancel()
                }
            }
        })

        // process removal operation
        this.map.once("pm:buttonclick", function (ev) {
            if (ev.btnName === "removalMode") {
                let ls = selectRectLayerGroup.getLayers()
                if (ls.length > 0) {
                    ls[0].remove()
                }
                this.map.pm.Toolbar.buttons.removalMode._clicked()
                //ev.button._clicked()
                if (onCancel) {
                    onCancel()
                }
            }
        }.bind(this))

        this.map.once("pm:create", function (ev) {
            let l = ev.layer
            this.enableRectangleResizeCapability(l)

            // enable resizing
            l.fire("dblclick")

            l.once("dblclick", function (e) {
                if (onFinish)
                    onFinish(l)
            })
        }.bind(this))
    }

    /**
     * enables resizing capability on rectangle
     * @param l
     * @param onFinish
     */
    enableRectangleResizeCapability(l, onFinish) {
        let enableEditing = function (l) {
            this.state.toggleMapState(MAP_STATE_EDIT_SELECT_REGION)
            l.setStyle({ pmIgnore: false }); // enable geoman editing for layer
            L.PM.reInitLayer(l);
            l.pm.enable()
        }.bind(this)

        if (l.enableLabels) {
            Object.assign(l.options.GD, {
                textPosition: "bottomright",
                fontSize: 16,
                cssClass: 'selectBoxLabelsDecor',
                displayLabelCb: function () {
                    return l.pm.enabled() ? "Double click to finish" : "Double click to resize"
                }
            })
            l.enableLabels()
            l.hideArea()
            l.hideLengthSegment()
        }

        //stop resizing on double click
        l.on("dblclick", function (e) {
            if (l.pm.enabled()) {
                l.pm.disable()
                if (onFinish) {
                    onFinish(l)
                }
            } else {
                enableEditing(l)
            }
            L.DomEvent.stopPropagation(e)
        })

        l.on("pm:disable", function () {
            this.state.toggleMapState(MAP_STATE_NORMAL)
            l.setStyle({ pmIgnore: true }); // disable geoman for layer when editing is finished
            L.PM.reInitLayer(l);
        }.bind(this))
    }

    enableRectangleResizeInnerBlockLimiter(l, innerBounds) {

        let resizeStartHandler = function (ev) {
            let boundingArea = innerBounds
            if (typeof innerBounds === "function") {
                boundingArea = innerBounds()
            }

            if (! boundingArea) {
                this._logdebug("inner bounds area isn't defined on resize operation")
                return true
            }

            let layerDefValidationDrag = ev.layer.pm._vertexValidationDrag
            let dLngRectOpCorner = 0
            let dLatRectOpCorner = 0
            let dLngBoundsOpCorner = 0
            let dLatBoundsOpCorner = 0
            let rectOpCorner = null
            let boundsOpCorner = null
            ev.layer.pm._vertexValidationDrag = function (draggedMarker) {
                let updateMarkers = function () {
                    this._layer.getLatLngs()[0].forEach((latlng, index) => {
                        const marker = this._cornerMarkers.find((m) => m._index === index);
                        if (marker) {
                            marker.setLatLng(latlng);
                        }
                    });
                }
                // inits oppositeCorners data on first drag event
                if (!dLngRectOpCorner && !dLatRectOpCorner) {
                    rectOpCorner = draggedMarker._oppositeCornerLatLng

                    dLngRectOpCorner = rectOpCorner.lng - draggedMarker._latlng.lng
                    dLatRectOpCorner = rectOpCorner.lat - draggedMarker._latlng.lat

                    // find closest opposite (from dragging marker) corner of bounding rectangle
                    let mdLng = 360
                    let mdLat = 180
                    let lat = 0
                    let lng = 0
                    for (let latlng of [boundingArea.getNorthWest(), boundingArea.getNorthEast(), boundingArea.getSouthWest(), boundingArea.getSouthEast()]) {
                        let d = Math.abs(latlng.lat - rectOpCorner.lat)
                        if (d < mdLat) {
                            mdLat = d
                            lat = latlng.lat
                        }
                        d = Math.abs(latlng.lng - rectOpCorner.lng)
                        if (d < mdLng) {
                            mdLng = d
                            lng = latlng.lng
                        }
                    }
                    boundsOpCorner = L.latLng(lat, lng)
                    dLngBoundsOpCorner = boundsOpCorner.lng - draggedMarker._latlng.lng
                    dLatBoundsOpCorner = boundsOpCorner.lat - draggedMarker._latlng.lat
                }

                if (// deny if marker lat or lng intersects bounding area
                    (draggedMarker._latlng.lng > boundingArea.getWest() && draggedMarker._latlng.lng < boundingArea.getEast()) ||
                    (draggedMarker._latlng.lat < boundingArea.getNorth() && draggedMarker._latlng.lat > boundingArea.getSouth()) ||
                    // deny if marker lat or lng moved outside opposite corner of rectangle
                    (rectOpCorner.lng - draggedMarker._latlng.lng >= 0 && dLngRectOpCorner < 0) ||
                    (rectOpCorner.lng - draggedMarker._latlng.lng <= 0 && dLngRectOpCorner > 0) ||
                    (rectOpCorner.lat - draggedMarker._latlng.lat >= 0 && dLatRectOpCorner < 0) ||
                    (rectOpCorner.lat - draggedMarker._latlng.lat <= 0 && dLatRectOpCorner > 0) ||
                    // deny if marker lat or lng moved outside opposite corner of bounding rectangle
                    (boundsOpCorner.lng - draggedMarker._latlng.lng >= 0 && dLngBoundsOpCorner < 0) ||
                    (boundsOpCorner.lng - draggedMarker._latlng.lng <= 0 && dLngBoundsOpCorner > 0) ||
                    (boundsOpCorner.lat - draggedMarker._latlng.lat >= 0 && dLatBoundsOpCorner < 0) ||
                    (boundsOpCorner.lat - draggedMarker._latlng.lat <= 0 && dLatBoundsOpCorner > 0)
                ) {
                    updateMarkers.bind(this)()
                    return false
                }
                // calls original _vertexValidationDrag
                return layerDefValidationDrag(draggedMarker)
            }.bind(ev.layer.pm)

            ev.marker.on("dragend", function () {
                //restore an original validation function
                if (layerDefValidationDrag)
                    ev.layer.pm._vertexValidationDrag = layerDefValidationDrag
            })

            return true
        }.bind(this)

        l.pm.setOptions({moveVertexValidation: resizeStartHandler})
    }

    enableGeometryDbClickEditAbility(l) {
        if (l instanceof L.Marker)
            return
        l.on("dblclick", function (ev) {
            if (ev.target.pm.enabled()) {
                ev.target.pm.disable()
            } else {
                ev.target.pm.enable()
            }
            L.DomEvent.stopPropagation(ev)
        })
    }

    enableVertexCoordinateEditAbility(l) {

        let fn = function (ev) {
            this.createVertexCoordinateEditDialog(ev.target)
        }.bind(this)

        if (l instanceof L.Marker) {
            l.on("dblclick", fn)
            return
        }

        let passOverArray = function (arr, cb) {
            if (L.Util.isFlat(arr)) {
                for (let marker of arr) {cb(marker)}
            } else {
                for (let el of arr) {passOverArray(el, cb)}
            }
        }

        l.on("pm:enable", function (ev) {
            // adds dblclick handlers to all vertex markers on edit start and removes these handlers on edit stop
            passOverArray(ev.layer.pm._markers, function (el) {
                el.on("dblclick", fn)
            })
        })

        l.on("pm:vertexadded", function (ev) {
            ev.marker.on("dblclick", fn)
        })

        l.on("pm:disable", function (ev) {
            // removes handlers from all vertex markers on edit stop
            passOverArray(ev.layer.pm._markers, function (el) {
                el.off("dblclick", fn)
            })
        })
    }

    createNewWorkspaceWindowDialog() {
        let UIC = new UICListCommand(
            {
                id: 'newWorkspace',
                title: 'Create new map',
                descr: "Select a region on a map or upload map from a file",
                items: [{
                    name: 'Select region',
                    picture: UITemplates.getContent('png-select-region'),
                    onSelect: function (id) {
                        this.createSelectionArea(function (layer) {
                            this.mapContext.createNewWorkspace(layer.getBounds())
                            layer.remove()

                        }.bind(this))
                        return true
                    }.bind(this)
                },
                    new UICFileUpload({worker: mapContext.RPCUploadEntity.bind(mapContext), uploadType: 'any'})
                ],
                // method to create parent dialogs
                factory: this.UIDialogFactory.createUICDialog.bind(this.UIDialogFactory)
            }
        )
        this.UIDialogFactory.createUICDialog(UIC, {/*onClose: function () {this.state.toggleMapState(MAP_STATE_NORMAL)}.bind(this)*/})
    }

    createDeleteWorkspaceWindowDialog(workspace) {
        if (! workspace.isMine() && ! workspace.isShared()) {
            return
        }

        let t = "Map will be deleted"
        let c = `Do you confirm deleting the map '${workspace.name}' and all related map data?`

        if (! workspace.isMine() && workspace.isShared()) {
            t = "Map will be removed"
            c = `Do you confirm removing the map '${workspace.name}' from your workspace?`
        }

        this.UIDialogFactory.modalYesNoWindow( {
            title: t,
            content: c,
            yesCaption: "Yes",
            noCaption: "No",
            yes: function () {
                this.mapContext.RPCDeleteWorkspace(workspace)
                return true;
            }.bind(this),
            no: function () {
                return true;
            },
        });
    }

    createFeaturesMoveToWorkspaceDialog(features, onYes) {
        let t = "The map is extended"
        let c = `There ${features.length === 1 ? "is":"are"} ${features.length} object${features.length === 1 ? "":"s"} in extended map area. Do you confirm to add ${features.length === 1 ? "it":"them"} to this map?`

        this.UIDialogFactory.modalYesNoWindow( {
            title: t,
            content: c,
            yesCaption: "Yes",
            noCaption: "No",
            yes: function () {
                if (onYes)
                    onYes()
                return true;
            }.bind(this),
            no: function () {
                return true;
            },
        });
    }

    createDeleteLayerWindowDialog(layer) {
        if (! layer || ! layer.workspace.isMine())
            return

        let t = "Layer will be deleted"
        let c = `Do you confirm deleting the layer '${layer.name}' ?`

        this.UIDialogFactory.modalYesNoWindow( {
            title: t,
            content: c,
            yesCaption: "Yes",
            noCaption: "No",
            yes: function () {
                this.mapContext.RPCDeleteLayer(layer)
                return true;
            }.bind(this),
            no: function () {
                return true;
            },
        });
    }

    createStyleEditorDialogForDrawing() {
        this.styleEditorDrawingStyle.setStyle(this.mapContext.drawingStyleContext.data())
        this.styleEditor.initChangeStyle(this.styleEditorDrawingStyle)
    }

    createStyleEditorDialogForVectorLayer(layer, styleKey) {
        let style = Object.assign({}, layer.getStyle(styleKey))
        let tmp = L.polygon([]) // tmporary leaflet feature storing current style
        // fill options panel is disabled if fill is false
        // enable fill options panel by setting fill = true and fillOpacity to zero
        // todo add radio button to styleEditor to enable/disable fill option
        if (style.fill === false) {
            style.fill = true;
            style.fillOpacity = "0";
            if (! ("fillColor" in style)) {
                style.fillColor = style.color
            }
        }
        tmp.setStyle(style)
        tmp._maphubVectorLayer = layer
        tmp.on('styleeditor:changed', function(leafletLayer){
            // save
            // disable fill
            let style = Object.assign({}, leafletLayer.options);
            if (style.fillOpacity === "0") {
                style.fill = false;
                delete style.fillOpacity
            }
            // update VectorLayer style
            layer.setStyle(style, styleKey)
        }.bind(this));
        this.sidebar.close()
        // limit line weight due to undesirable visual effects on TMS layer
        // if line weight > 3
        this.styleEditor.initChangeStyle(tmp, {weight: {max: 3}})
    }

    //fired on DOMContentLoaded event
    onDocumentLoad(event) {

    }

    /*
    return a preloaded template filled with options values
     */
    getContentTemplate(id, options, includes) {
        return UITemplates.getContent(id, options, includes);
    }

    //LatLng to String
    locationHuman(location, digits) {
        digits = digits === undefined ? 5 : digits;
        return location.lat.toFixed(digits) + ', ' + location.lng.toFixed(digits);
    }

    _logdebug(msg) {
        if (this.devMode)
            elog("DEBUG", msg)
    }

}

export {MapUI};
